import { FC, useContext, useMemo, useState, useCallback } from "react";
import { Table, Button, TableColumnsType, Popconfirm, message, Row, Col, Tag, Checkbox } from "antd";
import { DrawRequestApprovalType, DrawRequestResp } from "../../../../types/draw-request";
import { convertWithCommas } from "../../../../helpers/convertWithCommas";
import { DataContext } from "../../../../contexts/DataContext";
import moment from "moment";
import { ApproverResponse } from "../../../../types/approvers";
import { AuthContext } from "../../../../contexts/AuthContext";
import { PaymentMethod } from "../../../../types/payment-method";
import { drawRequestsService } from "../../../../services/draw-request";
import { CheckOutlined, CloseOutlined, CopyOutlined, DownloadOutlined, EditOutlined, StopOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router";
// import { useMsal } from "@azure/msal-react";
import { canBeApprovedOrDenied } from "../../helpers/canBeApprovedOrDenied";
import { canVoid } from "../../helpers/canVoid";
import { CheckboxProps } from "antd/es/checkbox";
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from "@tanstack/react-query";
import { SignatureModal } from "../../../../components/SignatureModal/SignatureModal";

interface IDrawRequestsTable {
  data: DrawRequestResp[];
  setData: React.Dispatch<React.SetStateAction<DrawRequestResp[]>>;
  onAction: (type: DrawRequestApprovalType, drawRequest?: DrawRequestResp) => void;
  loading: boolean;
  hideApproveAll: boolean;
  reFetchData: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<DrawRequestResp[], unknown>>;
}

export const DrawRequestsTableNew: FC<IDrawRequestsTable> = ({ data, onAction, setData, reFetchData, loading, hideApproveAll }) => {
  // const { accounts: accountsMsal } = useMsal();

  // const accountName = accountsMsal && accountsMsal.length ? accountsMsal[0]?.name : "";

  const {
    accounts,
    signatureContext: { setSignature, signature }
  } = useContext(DataContext);

  const { isTreasury, isApproveAllManualChecks, isPayment, userExtraInfo } = useContext(AuthContext);
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [signatureModal, setSignatureModal] = useState<boolean>(false);
  // const renderAccountOptions = (options: Account[]) => [

  const onAlreadyDownloadedModified = async (e: any, id: number) => {
    try {
      const value = e?.target?.checked || false;

      await drawRequestsService.modifyAlreadyDownloadedDrawRequest(id, value);
      setData((data) =>
        data.map((da) => {
          if (da.id === id)
            return {
              ...da,
              alreadyDownloaded: value
            };
          return da;
        })
      );

      message.success({ content: "Already download modified", key: "onAlreadyDownloadedModified" });
    } catch (error) {
      message.error({ content: "Ups, something went wrong, please try again later", key: "downloading" });
    }
  };

  const columns: TableColumnsType<DrawRequestResp> = useMemo(
    () => [
      {
        dataIndex: "submitDate",
        title: "Submit Date",
        key: "submitDate",
        width: "120px",
        align: "center",
        render: (text: string) => (text ? moment(text).format("MM/DD/YY") : "none"),
        sorter: (a: any, b: any) => {
          try {
            return new Date(b.submitDate).getTime() - new Date(a.submitDate).getTime();
          } catch (error) {
            return -1;
          }
        }
      },
      {
        title: "Due Date",
        dataIndex: "dueDate",
        key: "dueDate",
        width: "120px",
        align: "left",
        sorter: (a: any, b: any) => {
          try {
            return new Date(b.dueDate).getTime() - new Date(a.dueDate).getTime();
          } catch (error) {
            return -1;
          }
        },
        render: (text: string) => <div>{text ? moment(text).format("MM/DD/YY") : "none"}</div>
      },
      {
        dataIndex: "drawNumber",
        title: "Draw No./ Invoice No.",
        key: "drawNumber",
        width: "180px",
        align: "left"
      },
      {
        dataIndex: "amount",
        title: "Amount",
        key: "amount",
        width: "150px",
        align: "center",
        render: (value: number) => "$" + convertWithCommas(value)
      },
      {
        dataIndex: "payee",
        title: "Vendor Name",
        key: "vendorName",
        width: "200px",
        align: "center"
      },
      {
        dataIndex: "accountID",
        title: "MFA JV Name (Number)",
        key: "accountID",
        width: "300px",
        align: "center",
        render: (value: number) => {
          const account = accounts?.find((acc) => acc.accountID === value);
          if (!account) return <p>-Temporary-</p>;
          return <p>{`${account?.accountName} (${account?.glAccountCode})`}</p>;
        }
      },

      {
        dataIndex: "approvalStatus",
        title: "Approval Status",
        key: "approvalStatus",
        width: "250px",
        align: "center",
        render: (value: string) => {
          if (value?.toLowerCase() === "completed") {
            return <Tag color="success">{`${value ? value : "none"}`}</Tag>;
          } else if (value?.toLowerCase() === "rejected") {
            return <Tag color="error">{`${value ? value : "none"}`}</Tag>;
          } else if (value?.toLowerCase() === "voided") {
            return <Tag color="purple">{`${value ? value : "none"}`}</Tag>;
          }
          return <Tag color="blue">{`${value ? value : "none"}`}</Tag>;
        }
        // (value ? value : "none")
      },
      {
        dataIndex: "id",
        // title: "Payment Request (PDF)",
        title: "Review Payment Request",
        key: "paymentRequest",
        width: "200px",
        align: "center",
        render: (value: number) => (
          <Button
            onClick={() => {
              navigate(`preview-draw-request/${value}`);
            }}
            type="primary"
          >
            Review
          </Button>
        )
      },
      {
        dataIndex: "initiator",
        title: "Initiator",
        key: "initiator",
        width: "150px",
        align: "left"
      },
      {
        dataIndex: "paymentMethod",
        title: "Payment Type",
        key: "paymentMethod",
        width: "150px",
        align: "center",
        render: (value: PaymentMethod) => value.name
      },
      {
        dataIndex: "controlNumber",
        title: "Control Number",
        key: "controlNumber",
        width: "100px",
        align: "left",
        render: (value: string, row: DrawRequestResp) => {
          if (!value || value == "0") return `${row.id}-0`;
          return value;
        }
      },

      {
        title: (
          <>
            <div>
              <Row>
                <Col span={24}>
                  <p>Actions</p>
                </Col>
                <Col span={24}>
                  {isApproveAllManualChecks && (
                    <Popconfirm title="Are you sure you want to approve all manual checks?" onConfirm={() => setSignatureModal(true)}>
                      <Button style={{ padding: "5px" }} type="primary">
                        <CheckOutlined style={{ fontSize: "1.2rem" }} />
                        Approve all Manual Checks
                      </Button>
                    </Popconfirm>
                  )}
                </Col>
              </Row>
            </div>
          </>
        ),
        align: "center",
        dataIndex: "approvers",
        key: "actions",
        width: "250px",
        render: (value: ApproverResponse[], row: DrawRequestResp) => (
          <Row style={{ display: "flex", flexDirection: "row", justifyContent: "center" }}>
            {canBeApprovedOrDenied(row, isPayment, isTreasury) && (
              // <Popover
              //   visible={canBeApprovedOrDenied(row) ? false : undefined}
              //   content="This transaction can not be approved or denied by you or can not bet modify yet"
              // >
              <>
                <Popconfirm
                  title="Are you sure you want to approve this transaction?"
                  onConfirm={() => {
                    approveDenyTransaction(row.id, true);
                  }}
                >
                  <Button style={{ padding: "5px", marginRight: 5 }} type="primary">
                    <CheckOutlined style={{ fontSize: "1.2rem" }} />
                  </Button>
                </Popconfirm>

                <Popconfirm
                  title="Are you sure you want to deny this transaction?"
                  onConfirm={() => {
                    approveDenyTransaction(row.id, false);
                  }}
                >
                  <Button style={{ padding: "5px", marginRight: 5 }} type="primary" danger>
                    <CloseOutlined style={{ fontSize: "1.2rem" }} />
                  </Button>
                </Popconfirm>
                {/* </Popover> */}
              </>
            )}
            {canVoid(row, userExtraInfo) && (
              <Popconfirm
                title="Are you sure you want to void this draw request?"
                onConfirm={() => {
                  voidDrawRequest(row.id);
                }}
              >
                <Button style={{ padding: "5px", marginRight: 5 }} type="primary" danger>
                  <StopOutlined style={{ fontSize: "1.2rem" }} />
                </Button>
              </Popconfirm>
            )}
            {canEdit(row) && (
              <Button style={{ padding: "5px", marginRight: 5 }} type="primary" onClick={() => editDrawRequest(row.id, row)}>
                <EditOutlined style={{ fontSize: "1.2rem" }} />
              </Button>
            )}

            {canCopy(row) && (
              <Popconfirm
                title="Are you sure you want to copy this payment request?"
                onConfirm={() => {
                  copyDrawRequest(row.id);
                  // editDrawRequest(row.id, row, true);
                }}
              >
                <Button style={{ padding: "5px" }} type="primary">
                  <CopyOutlined style={{ fontSize: "1.2rem" }} />
                </Button>
              </Popconfirm>
            )}
          </Row>
        )
      },
      {
        dataIndex: "accountingApprovalStatus",
        title: "Admin Status",
        key: "status",
        width: "150px",
        align: "center",
        render: (value: string) => (
          <Tag
            color={value?.toLocaleLowerCase() === "approved" ? "success" : value?.toLocaleLowerCase() === "denied" ? "error" : "blue"}
          >{`${value ? value : "none"}`}</Tag>
        )
      },
      isPayment || isTreasury
        ? {
            dataIndex: "alreadyDownloaded",
            title: <DownloadOutlined />,
            key: "alreadyDownloaded",
            width: "80px",
            align: "center",
            sorter: (a: any, b: any) => {
              try {
                const aNumber = Number(!!a?.alreadyDownloaded);
                const bNumber = Number(!!b?.alreadyDownloaded);
                return aNumber - bNumber;
              } catch (error) {
                return -1;
              }
            },
            render: (value: boolean, row: DrawRequestResp) => (
              <>
                {!row.isDraft && row.approvalStatus?.toLowerCase() === "completed" && (
                  <Checkbox checked={!!row?.alreadyDownloaded} onChange={(e) => onAlreadyDownloadedModified(e, row.id)} />
                )}
              </>
            )
          }
        : {}
    ],
    [data, hideApproveAll, accounts, loading, isPayment, isTreasury, isPayment, userExtraInfo]
  );

  // const cannotBeModify = (drawRequest: DrawRequestResp) => {
  //   return drawRequest.accountingApprovalStatus?.toLowerCase() === "approve" && drawRequest.approvalStatus?.toLowerCase() === "complete";
  // };

  const approveDenyTransaction = async (id: number | string, isApprove: boolean = true) => {
    setIsLoading(true);
    try {
      const drawRequest = await drawRequestsService.approveDenyTransaction(id, isApprove);

      setData((old) => [...old.map((dr) => (dr.id === id ? drawRequest : dr))]);

      message.success(`transaction ${isApprove ? "approved" : "denied"}`);
    } catch (error) {
      console.error(error);
      message.error(
        `Ups: something was wrong ${
          isApprove ? "approving" : "denying"
        } the transaction, please try again later or contact the support team`
      );
    }
    setIsLoading(false);
  };

  const canEdit = useCallback(
    (drawRequest: DrawRequestResp) => {
      if (!drawRequest.isDraft) return false;
      return drawRequest.initiatorId === userExtraInfo?.id?.toString();
    },
    [userExtraInfo]
  );

  // const canEdit = (drawRequest: DrawRequestResp) => {
  //   console.log("can edit?", { initiator: drawRequest.initiatorId, userExtra: userExtraInfo?.id?.toString() });
  //   if (!drawRequest.isDraft) return false;

  //   console.log({ initiator: drawRequest.initiatorId, userExtra: userExtraInfo?.id?.toString() });

  //   return drawRequest.initiatorId === userExtraInfo?.id?.toString();
  // };

  const canCopy = useCallback(
    (drawRequest: DrawRequestResp) => {
      const approvalStatus = drawRequest?.approvalStatus?.toLowerCase();

      if (approvalStatus !== "voided" && approvalStatus !== "completed" && approvalStatus !== "rejected") return false;

      return drawRequest.initiatorId === userExtraInfo?.id?.toString();
    },
    [userExtraInfo]
  );

  // const canCopy = (drawRequest: DrawRequestResp) => {
  //   const approvalStatus = drawRequest?.approvalStatus?.toLowerCase();

  //   if (approvalStatus !== "voided" && approvalStatus !== "completed" && approvalStatus !== "rejected") return false;

  //   return drawRequest.initiatorId === userExtraInfo?.id?.toString();
  // };

  const approveAllManualChecks = async (signature: string) => {
    setIsLoading(true);
    try {
      const resp = await drawRequestsService.approveAllManualChecks(signature);
      if (!resp.ok)
        message.error(`Ups: something was wrong approving all transactions, please try again later or contact the support team`);
      else {
        await reFetchData();
        message.success(`All manual checks were approved`);
      }
    } catch (error) {
      console.error(error);
      message.error(`Ups: something was wrong approving all transactions, please try again later or contact the support team`);
    }

    setIsLoading(false);
  };

  const voidDrawRequest = async (id: number) => {
    try {
      setIsLoading(true);
      await drawRequestsService.voidDrawRequest(id);
      setData((old) => [
        ...old.map((dr) => {
          if (dr.id === id) return { ...dr, approvalStatus: "Voided" };
          return dr;
        })
      ]);
      message.success(`Draw request voided`);
    } catch (error) {
      console.error(error);
      message.error(`Ups: something was wrong voiding the draw request, please try again later or contact the support team`);
    }
    setIsLoading(false);
  };
  // const deleteDrawRequest = async (id: number) => {
  //   try {
  //     setIsLoading(true);
  //     await drawRequestsService.deleteDrawRequest(id);
  //     setData((old) => [...old.filter((dr) => dr.id !== id)]);
  //     message.success(`Draw request rejected`);
  //   } catch (error) {
  //     console.error(error);
  //     message.error(`Ups: something was wrong rejecting the draw request, please try again later or contact the support team`);
  //   }
  //   setIsLoading(false);
  // };

  const editDrawRequest = (id: number | string, drawRequest: DrawRequestResp, isCopy?: boolean) => {
    if (!isCopy)
      navigate(`edit-draw-request/${id}`, {
        state: { drawRequest }
      });
    else
      navigate(`copy-draw-request/${id}`, {
        state: { drawRequest }
      });
  };

  const copyDrawRequest = async (id: number | string) => {
    try {
      setIsLoading(true);
      const resp = await drawRequestsService.copyDrawRequest(id);
      if (!resp.ok)
        message.error(`Ups: something was wrong copying the payment request, please try again later or contact the support team`);
      else
        navigate(`edit-draw-request/${resp.data?.id}`, {
          state: { drawRequest: resp.data }
        });
    } catch (error) {
      console.error(error);
      message.error(`Ups: something was wrong copying the payment request, please try again later or contact the support team`);
    }
    setIsLoading(false);
  };

  return (
    // <Spin spinning={isLoading} tip="Working on it..." size="large">
    <Row>
      <Col
        span={24}
        // style={{ paddingLeft: "30px", paddingRight: "30px" }}
      >
        {/* {JSON.stringify({ isPayment, isTreasury })} */}
        <Table
          rowClassName={(record) => (record.isDraft ? "draft-row" : record.isNewPayee ? "new-payee-row" : "")}
          rowKey="id"
          sticky
          dataSource={data || []}
          columns={columns}
          loading={loading || isLoading}
          pagination={false}
          bordered
          scroll={{ x: 1000 }}
        />
      </Col>

      <SignatureModal
        currentSignature={signature && signature.length > 22 ? signature : ""}
        setSignature={setSignature}
        visible={signatureModal}
        setVisible={setSignatureModal}
        onSubmitSignature={approveAllManualChecks}
      ></SignatureModal>
    </Row>
    // </Spin>
  );
};
